/**
 * Keyboard navigation for the review grid.
 *
 * Navigation is EVENT-AWARE, mirroring the View-event panel:
 *   ←/→  step frame-by-frame (flat order, flowing across event boundaries)
 *   ↑/↓  jump to the previous/next event, landing on its first frame
 *
 * The helpers are pure and operate on flat indices into the (already
 * event-contiguously sorted) item list, so they work identically in both the
 * grid and one-event-per-row layouts. `currentIndex` is -1 when nothing is
 * focused yet (the first arrow press focuses the first frame).
 */

export type ArrowDirection = -1 | 1;

/**
 * Group consecutive same-`event_uuid` items into runs of their flat indices.
 * Items arrive event-contiguous (see `sortReviewItems`), so each maximal run
 * is one event. e.g. uuids [a,a,b,c,c] → [[0,1],[2],[3,4]].
 */
export const buildEventRuns = (
  items: ReadonlyArray<{ event_uuid: string }>,
): number[][] => {
  const runs: number[][] = [];
  let i = 0;
  while (i < items.length) {
    const uuid = items[i].event_uuid;
    const run: number[] = [];
    while (i < items.length && items[i].event_uuid === uuid) {
      run.push(i);
      i++;
    }
    runs.push(run);
  }
  return runs;
};

/**
 * Next focused frame for ←/→. Steps one card in flat order, flowing across
 * event boundaries, clamped to the list. From "no focus" (-1) the first press
 * focuses the first frame. Returns -1 only for an empty list.
 */
export const nextFrameIndex = (
  currentIndex: number,
  direction: ArrowDirection,
  count: number,
): number => {
  if (count === 0) return -1;
  if (currentIndex < 0) return 0;
  return Math.max(0, Math.min(count - 1, currentIndex + direction));
};

/**
 * Next focused frame for ↑/↓. Jumps to the previous/next event and lands on
 * its first frame. At the first/last event the press is a no-op (returns the
 * current index). From "no focus" (-1) the first press focuses the first
 * event's first frame. Returns -1 only for an empty run list.
 */
export const nextEventFirstFrame = (
  currentIndex: number,
  direction: ArrowDirection,
  runs: number[][],
): number => {
  if (runs.length === 0) return -1;
  if (currentIndex < 0) return runs[0][0];
  const runIndex = runs.findIndex((run) => run.includes(currentIndex));
  if (runIndex === -1) return runs[0][0];
  const target = runIndex + direction;
  if (target < 0 || target >= runs.length) return currentIndex; // clamp: no-op
  return runs[target][0];
};
